import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import * as moment from 'moment'
import { Subject, takeUntil } from 'rxjs'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { EntityWiseDataAPIModel, SelectedCompanyAPIModel } from 'src/app/models/api.model'
import { CommonService, getMonthOptions } from 'src/app/services/common/common.service'
import { DashboardService } from 'src/app/services/dashboard/dashboard.service'

@Component({
  selector: 'app-entity-wise-info',
  templateUrl: './entity-wise-info.component.html',
  styleUrls: ['./entity-wise-info.component.scss']
})
export class EntityWiseInfoComponent implements OnInit, OnDestroy {
  @Input() isVisible: boolean = false
  @Output() close = new EventEmitter<void>()

  classNames = ['main-boxone', 'main-boxtwo', 'main-boxthree', 'main-boxfour', 'main-boxfive']
  monthOptions: { value: string; key: string | null }[]
  fromDate: string
  toDate: string
  selectedMonth: string | null = null
  entityWiseData: EntityWiseDataAPIModel
  private readonly destroy$ = new Subject<void>()

  apiPayload = {
    companyEntities: [],
    from: null,
    to: null
  }

  constructor(private readonly cs: CommonService, private readonly dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.initializeMonthOptions()
    this.loadEntityWiseData()
  }

  openModal(): void {
    this.isVisible = true
  }

  closeModal(): void {
    this.isVisible = false
    this.close.emit()
  }

  handleCompanySelectedValue(company: SelectedCompanyAPIModel): void {
    this.apiPayload.companyEntities = this.getCompanyEntities(company)
    this.dispatchEntityWiseData()
  }

  onMonthChange($event: any): void {
    const dateFilter = this.buildDateFilter($event)
    this.resetDates()
    this.updateApiPayloadDates(dateFilter)
    if (this.apiPayload.companyEntities.length === 0) {
      this.cs.showError('Please select a company')
      return
    }
    this.dispatchEntityWiseData()
  }

  dateSelection(): void {
    this.setApiPayloadFromDate()
    this.setApiPayloadToDate()
    if (this.fromDate && this.toDate) {
      this.selectedMonth = null
      if (this.apiPayload.companyEntities.length === 0) {
        this.cs.showError('Please select a company')
        return
      }
      this.dispatchEntityWiseData()
    }
    if (!this.fromDate && !this.toDate) {
      this.setApiPayloadFromDate()
      this.setApiPayloadToDate()
      this.dispatchEntityWiseData()
    }
  }

  private initializeMonthOptions(): void {
    this.monthOptions = getMonthOptions()
    this.monthOptions.unshift({ value: 'Select Month', key: null })
  }

  private loadEntityWiseData(): void {
    this.dashboardService
      .getEntityWiseDataSelect()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: EntityWiseDataAPIModel) => {
        this.entityWiseData = data
      })
  }

  private dispatchEntityWiseData(): void {
    this.dashboardService.dispatchEntityWiseDataLoadAction(
      this.apiPayload.companyEntities,
      this.apiPayload.from,
      this.apiPayload.to
    )
  }

  private buildDateFilter(value: string): { begin: Date; end: Date } | null {
    if (!value) {
      return null
    }
    const [begin, end] = value.split('^').map(date => new Date(date))
    return { begin, end }
  }

  private resetDates(): void {
    this.fromDate = null
    this.toDate = null
  }

  private updateApiPayloadDates(dateFilter: { begin: Date; end: Date } | null): void {
    if (!dateFilter) {
      this.resetApiPayloadDates()
    } else {
      this.setApiPayloadDates(dateFilter)
    }
  }

  private resetApiPayloadDates(): void {
    this.apiPayload.from = null
    this.apiPayload.to = null
  }

  private setApiPayloadDates(dateFilter: { begin: Date; end: Date }): void {
    this.apiPayload.from = dateFilter.begin
    this.apiPayload.to = dateFilter.end
  }

  private setApiPayloadFromDate(): void {
    this.apiPayload.from = this.fromDate ? moment.utc(this.fromDate).startOf('day').toISOString() : null
  }

  private setApiPayloadToDate(): void {
    this.apiPayload.to = this.toDate ? moment.utc(this.toDate).endOf('day').toISOString() : null
  }

  private getCompanyEntities(company: SelectedCompanyAPIModel): string[] {
    return company.type === GlobalConstants.COMPANY_TYPE.GROUP
      ? company?.relatedEntities?.map(entity => entity.id)
      : [company?.id]
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.dashboardService.dispatchDeleteEntityWiseDataLoadAction()
  }
}
